import { useState, useEffect, useCallback } from "react";
import { usePageBack } from "../context/PageBackContext.js";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useMe } from "../context/MeContext.js";
import { AsyncButton } from "../components/ui/async-button.js";
import { Button } from "../components/ui/button.js";
import { Input } from "../components/ui/input.js";
import { Badge } from "../components/ui/badge.js";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog.js";
import { useToast } from "../context/ToastContext.js";
import EmailDeliveryLog from "./admin/EmailDeliveryLog.js";

import { ADMIN_ROLES as SUPER_HR } from "@hrms/types";

interface EmailStatus { enabled: boolean; from: string | null }

// ── Send-test dialog ─────────────────────────────────────────────────────────

function SendTestDialog({ template, status, defaultTo, onClose, onSent }: {
  template: EmailTemplate; status: EmailStatus | null; defaultTo: string;
  onClose: () => void; onSent: (to: string) => void;
}) {
  const { show: showToast } = useToast();
  const [to, setTo] = useState(defaultTo);
  const [error, setError] = useState("");

  async function sendTest() {
    setError("");
    if (!/^\S+@\S+\.\S+$/.test(to.trim())) { setError("Enter a valid email address."); throw new Error("validation"); }
    try {
      const r = await api.post<{ sentTo: string; from: string }>(`/admin/email-templates/${template.id}/send-test`, { to: to.trim() });
      showToast(`Test email sent to ${r.data.sentTo} from ${r.data.from}`, "success");
      onSent(to.trim());
    } catch (err) {
      setError(errMsg(err) ?? "The test email could not be sent.");
      logError("EmailTemplatesAdminPage.sendTest", err, { templateId: template.id });
      throw err;
    }
  }

  return (
    <Dialog open onOpenChange={open => { if (!open) onClose(); }}>
      <DialogContent className="max-w-115">
        <DialogHeader>
          <DialogTitle>Send a test email</DialogTitle>
          <DialogDescription>
            Sends <span className="font-mono">{template.type}</span> filled with sample data, with "[TEST]" in the subject.
            {status?.from && <> It goes out from <span className="font-semibold">{status.from}</span>.</>}
          </DialogDescription>
        </DialogHeader>
        <label htmlFor="send-test-to" className="flex flex-col gap-1 text-[12px] font-semibold text-surface-600">
          Send to
          <Input id="send-test-to" type="email" autoFocus placeholder="name@example.com" value={to}
            onChange={e => setTo(e.target.value)} />
        </label>
        {error && <p className="m-0 text-[12px] text-error">{error}</p>}
        <DialogFooter className="justify-start">
          <AsyncButton onClick={() => sendTest()} disabled={!to.trim() || !status?.enabled}>Send test</AsyncButton>
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface EmailTemplate {
  id: string; type: string; channel: string; language: string;
  subject: string | null; bodyHtml: string; description: string | null;
  isActive: boolean; updatedAt: string;
}

interface Signature {
  id: string; html: string; updatedAt: string | null;
}

const card: React.CSSProperties = {
  background: "var(--color-surface-0, #fff)",
  border: "1px solid var(--color-surface-200)",
  borderRadius: "var(--radius-lg)",
  padding: 20,
};

const inp: React.CSSProperties = {
  width: "100%", padding: "7px 10px",
  border: "1.5px solid var(--color-surface-200)", borderRadius: "var(--radius-md)",
  fontSize: 13, outline: "none", background: "var(--color-surface-0)", boxSizing: "border-box",
};

const lbl: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: "var(--color-surface-600)", display: "block", marginBottom: 4,
};

const btn: React.CSSProperties = {
  padding: "8px 16px", border: "none", borderRadius: "var(--radius-md)",
  background: "var(--color-brand-500)", color: "#fff",
  fontSize: 13, fontWeight: 600, cursor: "pointer",
};

const btnSm: React.CSSProperties = { ...btn, padding: "4px 10px", fontSize: 11 };

const ghost: React.CSSProperties = {
  ...btn, background: "var(--color-surface-100)", color: "var(--color-surface-700)",
};

const SAMPLE_VALUES: Record<string, string> = {
  firstName: "Priya", link: "#", companyName: "Integer Telecom", appBase: "https://example.com",
  docType: "PAN Card", reason: "Image is blurry and illegible",
  employeeCode: "EMP1042", issueTitle: "Login button unresponsive on Safari",
  testSteps: "1. Open Safari\n2. Click Login\n3. Confirm it responds",
  employeeName: "Priya Sharma", tasksList: "<li>Provision laptop</li><li>Create email account</li>",
  // Compensation emails (same samples as the API's send-test)
  headline: "New compensation revision", reasonLabel: "Annual appraisal", currentCtc: "₹6,00,000",
  proposedCtc: "6,60,000", changeText: "+10.0% vs current CTC", effectiveFrom: "1 Nov 2026",
  submittedBy: "Super Admin", decision: "approved", accentColor: "#16a34a", decidedBy: "Meera Iyer",
  comment: "Matches the appraisal letter", employeeId: "sample",
  nextStep: "It is now the employee's current compensation from the effective date.",
  version: "5", appliesFrom: "1 Nov 2026", note: "Basic raised to 55% from FY 2027",
  changesRows: '<tr><td style="padding:9px 12px;border-top:1px solid #e2e8f0">Basic %</td><td align="right" style="padding:9px 12px;border-top:1px solid #e2e8f0">50%</td><td align="right" style="padding:9px 12px;border-top:1px solid #e2e8f0;font-weight:700">55%</td></tr>',
  resultText: "Applied to all employees from 1 Nov 2026: 42 restructured, 3 open revisions recalculated, 5 unchanged, 1 skipped.",
};

function renderPreview(bodyHtml: string, signatureHtml: string): string {
  const withVars = bodyHtml.replace(/\{\{(\w+)\}\}/g, (_, key: string) => SAMPLE_VALUES[key] ?? `{{${key}}}`);
  return withVars + signatureHtml;
}

// ── Edit modal ───────────────────────────────────────────────────────────────

interface FormState {
  type: string; channel: string; language: string;
  subject: string; bodyHtml: string; description: string; isActive: boolean;
}

function EmptyForm(): FormState {
  return { type: "", channel: "email", language: "en", subject: "", bodyHtml: "", description: "", isActive: true };
}

function TemplateModal({
  editing, signatureHtml, onClose, onSaved,
}: {
  editing: EmailTemplate | null; signatureHtml: string; onClose: () => void; onSaved: () => void;
}) {
  const [form, setForm] = useState<FormState>(() =>
    editing
      ? { type: editing.type, channel: editing.channel, language: editing.language, subject: editing.subject ?? "", bodyHtml: editing.bodyHtml, description: editing.description ?? "", isActive: editing.isActive }
      : EmptyForm(),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const isNew = !editing;

  async function save() {
    setSaving(true); setError("");
    try {
      if (isNew) {
        await api.post("/admin/email-templates", form);
      } else {
        await api.patch(`/admin/email-templates/${editing.id}`, {
          subject: form.subject, bodyHtml: form.bodyHtml, description: form.description, isActive: form.isActive,
        });
      }
      onSaved();
    } catch (err) {
      setError(errMsg(err) ?? "Failed to save template");
      logError("EmailTemplatesAdminPage.save", err);
      throw err;
    } finally { setSaving(false); }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 }}>
      <div style={{ ...card, width: 720, maxHeight: "88vh", overflowY: "auto" }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>{isNew ? "New Email Template" : `Edit: ${editing.type}`}</h2>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div>
            <label style={lbl}>Type</label>
            <input style={inp} value={form.type} disabled={!isNew} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} placeholder="e.g. onboarding_portal_link" />
          </div>
          <div>
            <label style={lbl}>Channel</label>
            <input style={inp} value={form.channel} disabled={!isNew} onChange={e => setForm(f => ({ ...f, channel: e.target.value }))} />
          </div>
          <div>
            <label style={lbl}>Language</label>
            <input style={inp} value={form.language} disabled={!isNew} onChange={e => setForm(f => ({ ...f, language: e.target.value }))} />
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>Subject {form.channel === "email" && "(required)"}</label>
          <input style={inp} value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>Body ({"{{variable}}"} placeholders supported)</label>
          <textarea
            style={{ ...inp, fontFamily: "monospace", fontSize: 12 }}
            rows={14}
            value={form.bodyHtml}
            onChange={e => setForm(f => ({ ...f, bodyHtml: e.target.value }))}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>Description (admin help text — variables available, when it fires)</label>
          <textarea style={inp} rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, fontSize: 13 }}>
          <input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />
          Active
        </label>

        <div style={{ marginBottom: 16 }}>
          <label style={lbl}>Preview (sample data)</label>
          <div
            style={{ border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", padding: 16, background: "#fafafa" }}
            dangerouslySetInnerHTML={{ __html: renderPreview(form.bodyHtml, signatureHtml) }}
          />
        </div>

        {error && <div style={{ color: "var(--color-error)", fontSize: 12, marginBottom: 12 }}>{error}</div>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button style={ghost} onClick={onClose} disabled={saving}>Cancel</button>
          <AsyncButton onClick={() => save()} disabled={saving || !form.bodyHtml || (form.channel === "email" && !form.subject)}>
            Save
          </AsyncButton>
        </div>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function EmailTemplatesAdminPage() {
  const { role } = useMe();
  const navigate = useNavigate();
  usePageBack("/admin", "Back to Admin");

  useEffect(() => {
    if (role && !SUPER_HR.has(role)) navigate("/admin", { replace: true });
  }, [role, navigate]);

  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [signature, setSignature] = useState<Signature | null>(null);
  const [signatureDraft, setSignatureDraft] = useState("");
  const [handbookEmailEnabled, setHandbookEmailEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [savingSig, setSavingSig] = useState(false);
  const [savingHandbookSetting, setSavingHandbookSetting] = useState(false);
  const [editing, setEditing] = useState<EmailTemplate | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [emailStatus, setEmailStatus] = useState<EmailStatus | null>(null);
  const [testTarget, setTestTarget] = useState<EmailTemplate | null>(null);
  const [lastTestTo, setLastTestTo] = useState("");
  const [logRefresh, setLogRefresh] = useState(0);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tplR, sigR, handbookR, statusR] = await Promise.all([
        api.get<EmailTemplate[]>("/admin/email-templates"),
        api.get<Signature>("/admin/email-signature"),
        api.get<{ enabled: boolean }>("/admin/handbook-email-setting"),
        api.get<EmailStatus>("/admin/email-status"),
      ]);
      setTemplates(tplR.data);
      setSignature(sigR.data);
      setSignatureDraft(sigR.data.html);
      setHandbookEmailEnabled(handbookR.data.enabled);
      setEmailStatus(statusR.data);
    } catch (err) { logError("EmailTemplatesAdminPage.loadAll", err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { void loadAll(); }, [loadAll]);

  async function saveSignature() {
    setSavingSig(true);
    try {
      const r = await api.patch<Signature>("/admin/email-signature", { html: signatureDraft });
      setSignature(r.data);
    } catch (err) {
      logError("EmailTemplatesAdminPage.saveSignature", err);
      throw err;
    } finally { setSavingSig(false); }
  }

  async function toggleHandbookEmail(next: boolean) {
    setSavingHandbookSetting(true);
    try {
      const r = await api.patch<{ enabled: boolean }>("/admin/handbook-email-setting", { enabled: next });
      setHandbookEmailEnabled(r.data.enabled);
    } catch (err) { logError("EmailTemplatesAdminPage.toggleHandbookEmail", err); }
    finally { setSavingHandbookSetting(false); }
  }

  function openAdd() { setEditing(null); setShowModal(true); }
  function openEdit(t: EmailTemplate) { setEditing(t); setShowModal(true); }
  function closeModal() { setShowModal(false); setEditing(null); }
  function onSaved() { closeModal(); void loadAll(); }

  return (
    <div style={{ padding: "24px", maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 2 }}>Email Templates</h1>
        <p style={{ fontSize: 13, color: "var(--color-surface-500)" }}>
          Configure the content of every system email. Use "Send test" on a template to check how it arrives.
        </p>
        {emailStatus && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px] text-surface-600">
            {emailStatus.enabled
              ? <><Badge tone="success">Sending on</Badge> Emails go out through Amazon SES from <span className="font-semibold">{emailStatus.from}</span></>
              : <><Badge tone="warning">Sending off</Badge> Email is switched off on this environment (SES_FROM_EMAIL is not set), so tests can't be sent here.</>}
          </div>
        )}
      </div>

      <div style={{ ...card, marginBottom: 24 }}>
        <label style={lbl}>Global Signature (appended to every email)</label>
        <textarea style={{ ...inp, fontFamily: "monospace", fontSize: 12 }} rows={4} value={signatureDraft} onChange={e => setSignatureDraft(e.target.value)} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
          <div dangerouslySetInnerHTML={{ __html: signatureDraft }} style={{ fontSize: 12 }} />
          <AsyncButton size="sm" onClick={() => saveSignature()} disabled={savingSig || signatureDraft === signature?.html}>
            Save signature
          </AsyncButton>
        </div>
      </div>

      <div style={{ ...card, marginBottom: 24 }}>
        <label style={{ ...lbl, display: "flex", alignItems: "center", gap: 8, cursor: savingHandbookSetting ? "default" : "pointer" }}>
          <input
            type="checkbox"
            checked={handbookEmailEnabled}
            disabled={savingHandbookSetting}
            onChange={e => void toggleHandbookEmail(e.target.checked)}
          />
          Email employees a confirmation + link when they sign the handbook
        </label>
        <p style={{ fontSize: 12, color: "var(--color-surface-500)", margin: "6px 0 0" }}>
          Sent to the employee's personal email address at the moment they sign the handbook during onboarding.
        </p>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700 }}>Templates</h2>
        <button style={btn} onClick={openAdd}>Add Template</button>
      </div>

      {loading && <p style={{ color: "var(--color-surface-400)", fontSize: 13 }}>Loading…</p>}

      {!loading && (
        <div style={{ ...card, padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--color-surface-50)", textAlign: "left" }}>
                <th style={{ padding: "10px 14px" }}>Type</th>
                <th style={{ padding: "10px 14px" }}>Channel</th>
                <th style={{ padding: "10px 14px" }}>Language</th>
                <th style={{ padding: "10px 14px" }}>Subject</th>
                <th style={{ padding: "10px 14px" }}>Active</th>
                <th style={{ padding: "10px 14px" }}>Updated</th>
                <th style={{ padding: "10px 14px" }}></th>
              </tr>
            </thead>
            <tbody>
              {templates.map(t => (
                <tr key={t.id} style={{ borderTop: "1px solid var(--color-surface-100)" }}>
                  <td style={{ padding: "10px 14px", fontFamily: "monospace", fontSize: 12 }}>{t.type}</td>
                  <td style={{ padding: "10px 14px" }}>{t.channel}</td>
                  <td style={{ padding: "10px 14px" }}>{t.language}</td>
                  <td style={{ padding: "10px 14px" }}>{t.subject ?? <em style={{ color: "var(--color-surface-400)" }}>—</em>}</td>
                  <td style={{ padding: "10px 14px" }}>{t.isActive ? "Yes" : "No"}</td>
                  <td style={{ padding: "10px 14px" }}>{new Date(t.updatedAt).toLocaleDateString()}</td>
                  <td style={{ padding: "10px 14px" }}>
                    <div className="flex items-center justify-end gap-2">
                      <button style={btnSm} onClick={() => openEdit(t)}>Edit</button>
                      {t.channel === "email" && (
                        <Button size="sm" variant="secondary" className="h-6 px-2 text-[11px]" onClick={() => setTestTarget(t)}
                          disabled={!emailStatus?.enabled} title={emailStatus?.enabled ? "Send this template to an address of your choice" : "Email is switched off on this environment"}>
                          Send test
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <TemplateModal editing={editing} signatureHtml={signature?.html ?? ""} onClose={closeModal} onSaved={onSaved} />
      )}

      <EmailDeliveryLog refreshKey={logRefresh} />

      {testTarget && (
        <SendTestDialog template={testTarget} status={emailStatus} defaultTo={lastTestTo}
          onClose={() => { setTestTarget(null); setLogRefresh(k => k + 1); }}
          onSent={to => { setLastTestTo(to); setLogRefresh(k => k + 1); }} />
      )}
    </div>
  );
}
